/**
 * Anchor links for the product page's collapsible tabs (ClickUp 86d44vxf2).
 *
 * A link anywhere on the site can point at a tab and have it open, scroll into
 * view clear of anything pinned over the top of the page, and take keyboard
 * focus:
 *
 *     <a href="#tab-what-is-mini-delight">What is Mini Delight?</a>
 *     https://altenew.com/products/mini-delight-monthly-subscription-plan#tab-how-it-works
 *
 * The canonical form is a URL fragment, not a query string. A fragment is the
 * standard way to address a place inside a page (RFC 3986), it never reaches
 * the server so it cannot split the product into two crawlable URLs the way
 * ?tab= would, and because every tab also carries the matching element id it
 * still jumps to roughly the right place with this script blocked.
 *
 * Accepted, all resolving to the same tab, so older or hand-typed links work:
 *
 *     #tab-how-it-works     the canonical form, and the element's real id
 *     #how-it-works         the bare key
 *     #how-it-works-tab     the suffixed variant
 *     ?tab=how-it-works     rewritten to #tab-how-it-works on arrival
 *
 * The key is normally the handleized tab title, but a one-word parenthetical
 * wins, so "Frequently Asked Questions (FAQ)" is #tab-faq. See the doc block in
 * snippets/tab-anchor-keys.liquid, which builds the list each tab answers to.
 *
 * A fragment that already matches some other element on the page is left
 * alone — the browser's own behaviour wins, and no tab is hijacked.
 *
 * Targets are marked up in snippets/tab.liquid and snippets/tab-contact.liquid.
 */
(function () {
  'use strict';

  if (window.__collapsibleAnchors) return;
  window.__collapsibleAnchors = true;

  // Product pages run two different accordions: the collapsible tabs
  // (.collapsibles-wrapper + a button that theme.collapsibles animates) and the
  // native <details> rows in blocks/product-accordion-group.liquid. Both mark
  // their anchor the same way, so everything below works off the attribute and
  // only openTab/triggerFor care which one they are looking at.
  var SELECTOR = '[data-tab-anchor]';
  var PREFIX = 'tab-';
  var GAP = 16;
  // The landing spot keeps moving after the first pass: the panel animates its
  // height over 500ms, the site header slides in and back out as the theme
  // swaps it for the product bar, and lazy content inside the tab settles late.
  // Re-run across all of that rather than guessing one delay.
  var SETTLE_MS = [150, 500, 1000, 1800];
  // theme.collapsibles.init() binds the trigger on its own schedule, so an
  // early click can land before there is a handler to catch it.
  var OPEN_RETRIES = 12;
  var OPEN_RETRY_MS = 250;
  var NUDGE_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'];


  function tabs() {
    return Array.prototype.slice.call(document.querySelectorAll(SELECTOR));
  }

  function isVisible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /**
   * Templates that render the tab blocks twice (a sidebar copy and a full-width
   * copy) would emit the same id twice. Keep it on the copy that is on screen
   * and strip it from the others, so the document stays valid and the anchor
   * resolves to the tab the shopper can actually see.
   */
  function dedupeIds() {
    var seen = {};
    tabs().forEach(function (el) {
      var id = el.id;
      if (!id) return;
      var kept = seen[id];
      if (!kept) {
        seen[id] = el;
      } else if (!isVisible(kept) && isVisible(el)) {
        kept.removeAttribute('id');
        seen[id] = el;
      } else {
        el.removeAttribute('id');
      }
    });
  }

  function cleanKey(raw) {
    var key = String(raw || '').replace(/^#/, '');
    try {
      key = decodeURIComponent(key);
    } catch (e) {
      /* a malformed escape is not a tab key */
    }
    return key.toLowerCase().trim();
  }

  /**
   * data-tab-anchor holds every key a tab answers to, canonical first, so a tab
   * titled "Frequently Asked Questions (FAQ)" is reachable as #tab-faq without
   * breaking a link already written against the long handle.
   */
  function byKey(key) {
    if (!key) return null;
    var hit = null;
    tabs().forEach(function (el) {
      var keys = (el.getAttribute('data-tab-anchor') || '').split(/\s+/);
      if (keys.indexOf(key) === -1) return;
      if (!hit || (!isVisible(hit) && isVisible(el))) hit = el;
    });
    return hit;
  }

  /**
   * A fragment already owned by a real element belongs to the browser: return
   * the tab only when that element IS a tab. Otherwise fall back to matching
   * the key, tolerating the tab- prefix and the -tab suffix.
   */
  function resolve(raw) {
    var key = cleanKey(raw);
    if (!key) return null;

    var existing = document.getElementById(key);
    if (existing) return existing.closest ? existing.closest(SELECTOR) : null;

    var exact = byKey(key);
    if (exact) return exact;

    if (key.indexOf(PREFIX) === 0) {
      var unprefixed = byKey(key.slice(PREFIX.length));
      if (unprefixed) return unprefixed;
    }
    return byKey(key.replace(/-tabs?$/, ''));
  }


  /**
   * How far the top of the viewport is covered right now. Measured from what is
   * actually painted there rather than from a list of known selectors, so the
   * free shipping bar, the site header while it is still pinned, and any app bar
   * are all accounted for without naming any of them.
   *
   * Two things this has to get right, both learned the hard way on the PDP:
   *
   *  - They STACK. The shipping bar owns the top 40px and the header sits under
   *    it, 40 to 152, so probing one line finds the bar, misses the header, and
   *    the tab lands behind it. Each pass probes just below what has been found
   *    so far and keeps going while the next layer starts where the last ended.
   *  - A hidden bar still has a box. Past the buy area, product-template.liquid
   *    puts .hide on #SiteHeader (opacity 0, visibility hidden) and swaps in the
   *    bottom product bar — but getBoundingClientRect still reports its 112px.
   *    Offsetting for that leaves a gap with nothing in it, so anything not
   *    actually painted is skipped.
   */
  function obstruction(target) {
    var vw = window.innerWidth || document.documentElement.clientWidth;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    if (!document.elementsFromPoint) return 0;

    var xs = [Math.min(8, vw - 1), Math.round(vw / 2), Math.max(vw - 8, 0)];
    var max = 0;

    for (var pass = 0; pass < 6; pass++) {
      var next = max;
      xs.forEach(function (x) {
        document.elementsFromPoint(x, max + 2).forEach(function (el) {
          if (el === document.body || el === document.documentElement) return;
          if (target && (el === target || el.contains(target))) return;

          var style = window.getComputedStyle(el);
          if (style.position !== 'fixed' && style.position !== 'sticky') return;
          if (style.visibility === 'hidden' || parseFloat(style.opacity) === 0) return;

          var rect = el.getBoundingClientRect();
          // Butts up against what we have already cleared, and is a bar rather
          // than a full-screen overlay or a tall sticky column.
          if (rect.top > max + 4 || rect.bottom <= max) return;
          if (rect.bottom > vh * 0.5) return;
          if (rect.bottom > next) next = rect.bottom;
        });
      });
      if (next === max) break;
      max = next;
    }
    return max;
  }

  function scrollToTab(wrapper) {
    var offset = obstruction(wrapper) + GAP;
    // Keeps a native jump, and anything else calling scrollIntoView, clear of
    // the same obstruction.
    wrapper.style.scrollMarginTop = offset + 'px';

    var top = window.pageYOffset + wrapper.getBoundingClientRect().top - offset;
    top = Math.max(0, Math.round(top));
    if (Math.abs(top - window.pageYOffset) < 2) return;

    if ('scrollBehavior' in document.documentElement.style) {
      window.scrollTo({ top: top, behavior: reducedMotion() ? 'auto' : 'smooth' });
    } else {
      window.scrollTo(0, top);
    }
  }

  /**
   * The row's own control: the collapsible tab's button, or the <details>
   * summary. Either is natively focusable, so focus can go straight to it.
   */
  function triggerFor(wrapper) {
    return wrapper.querySelector('.collapsible-trigger') || wrapper.querySelector('summary');
  }

  function openTab(wrapper, attempt) {
    var details = wrapper.querySelector('details');
    if (details) {
      details.open = true;
      return;
    }

    var trigger = wrapper.querySelector('.collapsible-trigger');
    if (!trigger || trigger.classList.contains('is-open')) return;

    trigger.click();
    if (trigger.classList.contains('is-open')) return;

    // Nothing was listening yet — theme.collapsibles.init() has not run, or the
    // module was mid-transition and swallowed the click.
    var next = (attempt || 0) + 1;
    if (next <= OPEN_RETRIES) {
      window.setTimeout(function () {
        openTab(wrapper, next);
      }, OPEN_RETRY_MS);
    }
  }

  /**
   * The trigger is the accordion's header and is natively focusable, so the
   * shopper's next Tab continues from the tab they asked for and a screen reader
   * announces the heading and its expanded state. Only claimed while nothing
   * else holds focus — never taken back off a shopper who has moved on.
   */
  function focusTrigger(wrapper) {
    var trigger = triggerFor(wrapper);
    if (!trigger) return;
    var active = document.activeElement;
    if (active && active !== document.body && active !== document.documentElement) return;
    trigger.focus({ preventScroll: true });
  }

  function arrive(wrapper) {
    focusTrigger(wrapper);
    scrollToTab(wrapper);
  }

  function go(raw) {
    var wrapper = resolve(raw);
    if (!wrapper) return false;

    openTab(wrapper);
    arrive(wrapper);

    // Everything above can still be in motion. Re-run until it has settled —
    // unless the shopper has taken over, in which case leave them alone.
    var interrupted = false;
    var stop = function () {
      interrupted = true;
    };
    NUDGE_EVENTS.forEach(function (type) {
      window.addEventListener(type, stop, { passive: true, once: true });
    });

    SETTLE_MS.forEach(function (delay) {
      window.setTimeout(function () {
        if (!interrupted) arrive(wrapper);
      }, delay);
    });
    window.setTimeout(function () {
      NUDGE_EVENTS.forEach(function (type) {
        window.removeEventListener(type, stop);
      });
    }, SETTLE_MS[SETTLE_MS.length - 1] + 100);

    return true;
  }

  /**
   * ?tab=how-it-works arrives from links that could not carry a fragment (some
   * email and ad tooling strips it). Swap it for the canonical fragment so what
   * the shopper copies out of the address bar is the form we document.
   */
  function adoptQueryParam() {
    if (!window.URLSearchParams || !window.history || !history.replaceState) return null;

    var params = new URLSearchParams(window.location.search);
    var value = params.get('tab');
    if (!value || !resolve(value)) return null;

    var key = cleanKey(value);
    if (key.indexOf(PREFIX) !== 0) key = PREFIX + key;

    params.delete('tab');
    var query = params.toString();
    history.replaceState(
      null,
      '',
      window.location.pathname + (query ? '?' + query : '') + '#' + key
    );
    return key;
  }

  function onClick(event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var link = event.target.closest ? event.target.closest('a[href]') : null;
    if (!link || link.target === '_blank' || link.hasAttribute('download')) return;

    var url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (e) {
      return;
    }
    if (!url.hash || url.host !== window.location.host) return;
    if (url.pathname !== window.location.pathname) return;
    if (!resolve(url.hash)) return;

    event.preventDefault();
    // Pushed rather than replaced so Back returns the shopper to where they were.
    if (window.location.hash !== url.hash && window.history && history.pushState) {
      history.pushState(null, '', url.hash);
    }
    go(url.hash);
  }

  function start() {
    dedupeIds();

    var initial = adoptQueryParam() || window.location.hash;
    if (initial) go(initial);

    document.addEventListener('click', onClick);
    window.addEventListener('hashchange', function () {
      if (window.location.hash) go(window.location.hash);
    });
    window.addEventListener('popstate', function () {
      if (window.location.hash) go(window.location.hash);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
